import { createContext, useContext, useMemo } from "react";
import { useSelector } from "react-redux";

import sumBy from "lodash/sumBy";

import useFetchKitItems from "@hooks/kit/use-fetch-kit-items";

import { getCountry } from "@selectors/country";

import { KIT_TYPE_GLOBAL, KIT_TYPE_US, WAREHOUSE_ITEM } from "@stadium/constants/kits";

const DEFAULT_OBJ = {};

export const KitContext = createContext({});

export const useKitItemsContext = () => useContext(KitContext);

const KitItemsProvider = ({ children }) => {
  const country = useSelector(getCountry);

  const {
    kitZonesMapping,
    totalProductCount,
    loading,
    lineItems,
    variantData,
    bundleProducts,
    bundleShop,
  } = useFetchKitItems();

  const selectedCountryKit = kitZonesMapping[country?.iso?.toLowerCase()]?.[0] || DEFAULT_OBJ;
  const kitState = bundleShop?.bundleState;

  const { [KIT_TYPE_US]: usKit = DEFAULT_OBJ, [KIT_TYPE_GLOBAL]: globalKit = DEFAULT_OBJ } =
    bundleProducts || DEFAULT_OBJ;

  const totalUsWarehouseProducts = useMemo(() => sumBy(usKit[WAREHOUSE_ITEM], "quantity"), [usKit]);

  const totalGlobalWarehouseProducts = useMemo(
    () => sumBy(globalKit[WAREHOUSE_ITEM], "quantity"),
    [globalKit],
  );

  const hasWarehouseProducts = Boolean(totalUsWarehouseProducts || totalGlobalWarehouseProducts);

  const value = useMemo(
    () => ({
      lineItems,
      totalProductCount,
      loading,
      variantData,
      kitZonesMapping,
      selectedCountryKit,
      bundleProducts,
      bundleShop,
      kitState,
      hasWarehouseProducts,
    }),
    [
      lineItems,
      totalProductCount,
      loading,
      variantData,
      kitZonesMapping,
      country,
      bundleProducts,
      hasWarehouseProducts,
    ],
  );

  return <KitContext.Provider value={value}>{children}</KitContext.Provider>;
};

export default KitItemsProvider;
